import { useQuery } from "@tanstack/react-query";
import { createCRUDHooks } from "@/lib/hooks-factory";
import { staffLevelsApi, staffLevelsOptionsApi, type StaffLevelOption } from "@/api/staff/staff-levels-api";
import type {
  StaffLevel,
  StaffLevelCreate,
  StaffLevelUpdate,
} from "@/types/staff/api-types/staff-level-types";
import type { CRUDApiParams } from "@/lib/api-factory";

interface StaffLevelSearchParams extends CRUDApiParams {
  page?: number;
  size?: number;
  name?: string;
  active_only?: boolean;
}

const hooks = createCRUDHooks<StaffLevel, StaffLevelCreate, StaffLevelUpdate, StaffLevelSearchParams>({
  queryKey: "staff-levels",
  api: staffLevelsApi,
  invalidateKeys: ["staff"],
  mapToOption: (level: StaffLevel) => ({
    value: level.id,
    label: level.name,
  }),
  getLoadParams: (search: string, page: number, size: number): StaffLevelSearchParams => ({
    page,
    size,
    name: search || undefined,
    active_only: true,
  }),
});

export const useGetStaffLevelsPaginated = hooks.useGetPaginated;
export const useCreateStaffLevel = hooks.useCreate;
export const useUpdateStaffLevel = hooks.useUpdate;
export const useDeleteStaffLevel = hooks.useDelete;

const staffLevelKeys = {
  all: ["staff-levels"] as const,
  options: () => [...staffLevelKeys.all, "options"] as const,
};

export function useStaffLevelOptions() {
  return useQuery({
    queryKey: staffLevelKeys.options(),
    queryFn: async (): Promise<StaffLevelOption[]> => {
      return await staffLevelsOptionsApi.getStaffLevelOptions();
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
